"use client";

import { useMemo } from "react";

import { maxComboOptionInstruments } from "#/config/combo";
import { useComboInstrumentsController } from "#/hooks/useComboInstruments.controller";
import { useNettedOptionUserPortfolio } from "#/services/kyan/hooks/useNettedUserPortfolio";
import { useOrderbook } from "#/services/kyan/hooks/useOrderbook";
import { useComboState } from "#/store/combo/comboState";
import { useComboType } from "#/store/combo/comboType";
import { RiskPosition } from "#/types/positions";
import {
  createOptionInstrumentName,
  isOptionInstrument,
} from "#/utils/instrument.utils";

import { getColumns } from "./InstrumentsTable.constants";
import {
  getComboInstrumentNames,
  updateComboInstrumentsByLegPrice,
} from "./InstrumentsTable.utils";

export const useInstrumentsTableController = () => {
  const {
    comboInstruments,
    comboPerpInstruments,
    comboOptionInstruments,
    handleAddLeg,
    handleDelete,
    handleChange,
    handleAddPerp,
    handleDeleteAll,
  } = useComboInstrumentsController();
  const nettedOptionPortfolio = useNettedOptionUserPortfolio({
    option: null,
    market: undefined,
    inputs: [],
  }) as RiskPosition[];

  const { setComboState } = useComboState();
  const { comboType, setComboType } = useComboType();
  const { data: orderbook, isLoading } = useOrderbook(
    getComboInstrumentNames(comboInstruments),
  );
  const updatedComboInstruments = updateComboInstrumentsByLegPrice(
    comboInstruments,
    orderbook,
  );

  const hideButton = !comboInstruments?.length;
  const isRfqOptionsTab = comboType === "rfq-options";
  const glowEffectIndex = comboInstruments.findIndex((item) => !item.size);

  const handleSizeChange = (rowId: string, value: string) => {
    const index = comboInstruments.findIndex((item) => item.id === rowId);
    if (index !== -1) handleChange(index, "size", value);
  };

  const columns = getColumns({
    showDeleteAll: comboInstruments.length > 0,
    glowEffectIndex,
    isRfqOptionsTab,
    isLoading,
    nettedOptionPortfolio,
    handleDelete,
    handleChange,
    handleDeleteAll,
    handleSizeChange,
  });

  const haveAllInstrumentsSizes = comboInstruments
    .filter((item) => !isRfqOptionsTab || isOptionInstrument(item.type))
    .every((item) => item.size);

  const hasDuplicatedInstruments = useMemo(() => {
    const seen = new Set<string>();
    for (const item of comboOptionInstruments) {
      const instrumentName = createOptionInstrumentName(item);
      if (seen.has(instrumentName)) return true;
      seen.add(instrumentName);
    }
    return false;
  }, [comboOptionInstruments]);

  const handleSubmit = () => {
    if (!isRfqOptionsTab) {
      setComboState("prices");
    }
  };

  return {
    columns,
    comboType,
    isLoading: !orderbook.length && isLoading,
    hideButton,
    isRfqOptionsTab,
    comboInstruments: updatedComboInstruments,
    comboPerpInstruments,
    comboOptionInstruments,
    maxComboOptionInstruments,
    haveAllInstrumentsSizes,
    hasDuplicatedInstruments,
    setComboType,
    handleAddLeg,
    handleAddPerp,
    handleSubmit,
  };
};
